/**
 * Tiny app-route helpers for the FE Web Service shell.
 *
 * `/auth/callback` remains a special OIDC path handled in App before
 * route rendering. Production deep-link fallback belongs to the public
 * deployment phase; Vite already serves these paths in local dev.
 */
export type AppRoute =
  | "home"
  | "dashboard"
  | "chat"
  | "compute"
  | "cli"
  | "settings"
  | "not-found";

export interface ProductLane {
  route: Exclude<AppRoute, "not-found">;
  path: string;
  label: string;
  eyebrow: string;
  title: string;
  description: string;
  cta: string;
  requiresAuth: boolean;
}

export const PRODUCT_LANES = [
  {
    route: "chat",
    path: "/chat",
    label: "Chat",
    eyebrow: "cheap inference",
    title: "Run AI R&D chat on open models",
    description:
      "Start with gpt-oss:120b, MCP tools, and guided inference before you reach for heavier compute.",
    cta: "open chat",
    requiresAuth: true,
  },
  {
    route: "compute",
    path: "/compute",
    label: "Compute",
    eyebrow: "gpu/cpu sandbox",
    title: "Launch research compute",
    description:
      "Spin up CPU and GPU sandboxes for experiments without stitching together a separate GPU provider.",
    cta: "open compute",
    requiresAuth: true,
  },
  {
    route: "cli",
    path: "/cli",
    label: "CLI",
    eyebrow: "agent automation",
    title: "Authenticate z01 CLI workflows",
    description:
      "Use the web account as the doorway into local agent launches, automation, and future CLI billing.",
    cta: "open cli",
    requiresAuth: false,
  },
] as const satisfies readonly ProductLane[];

export const SETTINGS_ROUTE: ProductLane = {
  route: "settings",
  path: "/settings",
  label: "Settings",
  eyebrow: "platform tools",
  title: "Connect MCP tools",
  description:
    "Register MCP servers and inspect the tool catalog available to chat.",
  cta: "open settings",
  requiresAuth: true,
};

export function routeFromPath(pathname: string): AppRoute {
  const path = normalizePath(pathname);
  // `/chat/<threadId>` routes resolve to the chat lane; the thread id
  // is exposed separately via chatThreadIdFromPath() so the page can
  // hydrate from /api/v1/conversations/:id without changing the route.
  if (path === "/chat" || path.startsWith("/chat/")) return "chat";
  switch (path) {
    case "/":
      return "home";
    case "/dashboard":
      return "dashboard";
    case "/compute":
    case "/vm":
    case "/vms":
      return "compute";
    case "/cli":
      return "cli";
    case "/settings":
      return "settings";
    default:
      return "not-found";
  }
}

/**
 * Extract the thread id from a `/chat/<id>` path. Returns `null` for
 * `/chat`, non-chat paths, or empty segments. Validates a relaxed UUID
 * shape — a malformed segment is treated as "no thread" rather than a
 * 404, so a typo doesn't strand the user on an error screen.
 */
export function chatThreadIdFromPath(pathname: string): string | null {
  const path = normalizePath(pathname);
  const m = path.match(/^\/chat\/([^/]+)$/);
  if (!m) return null;
  const id = decodeURIComponent(m[1] ?? "");
  if (!/^[0-9a-fA-F-]{8,}$/.test(id)) return null;
  return id;
}

export function pathForChatThread(threadId: string): string {
  return `/chat/${encodeURIComponent(threadId)}`;
}

export function pathForRoute(route: AppRoute): string {
  switch (route) {
    case "home":
      return "/";
    case "dashboard":
      return "/dashboard";
    case "chat":
      return "/chat";
    case "compute":
      return "/compute";
    case "cli":
      return "/cli";
    case "settings":
      return "/settings";
    case "not-found":
      return "/";
  }
}

function normalizePath(pathname: string): string {
  const withoutQuery = pathname.split(/[?#]/, 1)[0] ?? "/";
  if (withoutQuery.length <= 1) return "/";
  return withoutQuery.replace(/\/+$/, "");
}
